import { unwrapResult } from "@reduxjs/toolkit"
import { createContext, useContext } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { showConfirmModal } from "@/components/ConfirmModal"
import { ContextMenu, createContextMenu } from "@/components/ContextMenu"
import { requestAssignOrder, selectOrderById } from "@/store/orders"
import { Order } from "@/types"
import { OrderUtils } from "@/utils"

import { EditOrderPanel } from "../EditOrderPanel"
import { CancelOrderModal } from "./partials/CancelOrderModal"
import { MoveOrderToBufferModal } from "./partials/MoveOrderToBufferModal"
import { OrderDetailsPanel } from "./partials/OrderDetailsPanel"
import { OrderReturnModal } from "./partials/OrderReturnModal"
import { ReassignOrderModal } from "./partials/ReassignOrderModal"

export type OrderContextMenuAction = {
  action: "reassign_order" | "cancel_order" | "edit_order" | "return_order" | "show_details_panel" | "move_to_buffer"
  orderId: Order["id"]
} | null

export const OrderItemContext = createContext<{
  orderContextMenuAction: OrderContextMenuAction
  setOrderContextMenuAction: (action: OrderContextMenuAction) => void
} | null>(null)

export const {
  ContextMenuWrapper: OrdersContextMenuWrapper,
  useContextMenuEvent: useOrdersContextMenuEvent,
  useContextMenuTrigger: useOrdersContextMenuTrigger,
} = createContextMenu<{
  orderId: Order["id"]
}>("ordersPanel")

export const OrderContextMenu = () => {
  return (
    <>
      <OrdersContextMenuWrapper>
        <ContextMenuItems />
      </OrdersContextMenuWrapper>

      <ContextMenuActions />
    </>
  )
}

export const ContextMenuItems = () => {
  const { setOrderContextMenuAction } = useContext(OrderItemContext) || {}

  const {
    data: { orderId },
  } = useOrdersContextMenuEvent()

  const order = useAppSelector(selectOrderById(orderId))

  const dispatch = useAppDispatch()

  const { t } = useTranslation("orders")

  async function assignOrder(orderId: Order["id"]) {
    const res = await dispatch(requestAssignOrder(orderId)).then(unwrapResult)

    if (res.ok) toast.success(t("assign_order_success"))
  }

  if (!order || !setOrderContextMenuAction) return null

  return (
    <ContextMenu>
      <ContextMenu.Item
        iconName="route-alt"
        disabled={OrderUtils.isFinished(order)}
        onClick={() => setOrderContextMenuAction({ action: "reassign_order", orderId })}
      >
        Przepnij
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="close-circle"
        disabled={OrderUtils.isFinished(order)}
        onClick={() => setOrderContextMenuAction({ action: "cancel_order", orderId })}
      >
        Anuluj
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="rocket"
        disabled={!OrderUtils.canAssignOrder(order)}
        onClick={() => {
          showConfirmModal({
            title: "Przypisanie zlecenia",
            description: "Czy chcesz przypisać to zlecenie?",
            handleConfirm: () => assignOrder(order.id),
            autoFocusConfirm: true,
          })
        }}
      >
        Przypisz
      </ContextMenu.Item>

      <ContextMenu.Item iconName="edit" onClick={() => setOrderContextMenuAction({ action: "edit_order", orderId })}>
        Edytuj zlecenie
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="reply"
        disabled={!OrderUtils.canReturnOrder(order)}
        onClick={() => setOrderContextMenuAction({ action: "return_order", orderId })}
      >
        Zwrot do nadawcy
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="reply"
        disabled={!OrderUtils.canMoveToBuffer(order)}
        onClick={() => setOrderContextMenuAction({ action: "move_to_buffer", orderId })}
      >
        Cofnij do bufora
      </ContextMenu.Item>
    </ContextMenu>
  )
}

const ContextMenuActions = () => {
  const { orderContextMenuAction, setOrderContextMenuAction } = useContext(OrderItemContext) || {}

  if (!orderContextMenuAction || !setOrderContextMenuAction) return null

  const { action, orderId } = orderContextMenuAction

  function handleClose() {
    setOrderContextMenuAction?.(null)
  }

  switch (action) {
    case "reassign_order":
      return <ReassignOrderModal orderId={orderId} handleClose={handleClose} />
    case "cancel_order":
      return <CancelOrderModal orderId={orderId} handleClose={handleClose} />
    case "return_order":
      return <OrderReturnModal orderId={orderId} handleClose={handleClose} />
    case "show_details_panel":
      return <OrderDetailsPanel orderId={orderId} handleClose={handleClose} />
    case "move_to_buffer":
      return <MoveOrderToBufferModal orderId={orderId} handleClose={handleClose} />
    case "edit_order":
      return <EditOrderPanel orderId={orderId} handleClose={handleClose} />
  }
}
